import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchLearningMap } from '../services/api';
import { track } from '../services/track';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { getCopy } from '../utils/copy';
import { STAGE_LABEL, speciesEmoji } from '../utils/guardian';
import { describeNextReview } from '../utils/reviewTime';

/**
 * 六艺书院(/map):孩子自己选学什么
 *
 * 信息架构只有一条主线:六艺是「馆」,学科是馆里的「门」。
 *   - 六艺负责世界观和收集感(印章、勋章),但每一馆都直接摆出学科,孩子不用猜;
 *   - 学科用大家都认识的名字(语文、地理……),再配一句好玩的副标题,既好找又不枯燥;
 *   - 小游戏放进它练的那一馆(七巧板→御,图解数学→数),画成月洞门,不计进度;
 *   - 乐暂无内容,只在顶部印章条里占位。
 * 页面层级:灵兽问候 → 继续冒险(主按钮) → 六艺印章条(总览+跳转) → 五座馆。
 *
 * 配色:宣纸底 + 墨色线条 + 传统淡彩。线条用墨色而不是纯黑,颜色都压低饱和度,
 * 只有「继续冒险」用最亮的杏黄,保证它是页面上最显眼的东西。
 */

// 全页公用色
export const PALETTE = {
  paper: '#F7F0E1', // 宣纸
  ink: '#3B302B', // 墨(线条、正文)
  inkSoft: '#8A7B6E', // 淡墨(说明文字)
  wall: '#FFFCF5', // 粉墙
  pillar: '#C9785B', // 檐柱(淡朱)
  step: '#DCD2C0', // 石阶
  plaque: '#4A3A31', // 匾额
  gold: '#F0C75E', // 泥金(匾额字、门环、主按钮)
  wood: '#EBCFA5', // 原木门
  woodEdge: '#C99C6A', // 门框/门缝
  doneDoor: '#F6DC8C', // 学完:金门
};

// 每一馆的主色(屋顶、印章)和墙脚淡色;取传统色并压低饱和度
export const HALLS = {
  mathematics: { name: '算学馆', motto: '算一算,想一想', color: '#D9776B', tint: '#FBEDEA' }, // 胭脂
  calligraphy: { name: '文墨馆', motto: '读写古今中外', color: '#9686C2', tint: '#F1EEF8' }, // 藕荷
  rites: { name: '明礼馆', motto: '知古今,懂生活', color: '#DDA24E', tint: '#FBF1E1' }, // 杏黄
  charioteering: { name: '行旅馆', motto: '看空间,走九州', color: '#6AA4B5', tint: '#E9F3F5' }, // 天青
  archery: { name: '博物馆', motto: '认得鸟兽草木', color: '#7FAE86', tint: '#ECF4EC' }, // 竹青
  music: { name: '乐府', motto: '即将开放', color: '#B8AFA4', tint: '#F3F0EC' },
};

// 学科:图标 + 一句好玩的副标题;名字本身沿用后端的 label(大家都认识)
export const SUBJECTS = {
  math: { icon: '🔢', tagline: '推理、找规律' },
  science: { icon: '🔬', tagline: '万物为什么' },
  poetry: { icon: '📜', tagline: '拼回千年诗句' },
  chinese: { icon: '📖', tagline: '字词与成语' },
  english: { icon: '🔤', tagline: '单词小闯关' },
  history: { icon: '🏯', tagline: '穿越朝代' },
  life: { icon: '🧭', tagline: '生活小本领' },
  culture: { icon: '🏮', tagline: '节日与习俗' },
  spatial: { icon: '🧊', tagline: '立体想象' },
  geography: { icon: '🗺️', tagline: '走遍山河' },
  province: { icon: '🧩', tagline: '省份拼图' },
  nature: { icon: '🌱', tagline: '认识大自然' },
  biology: { icon: '🦋', tagline: '鸟兽草木' },
};

// 书院小游戏:不计入闯关进度,放进它练的那一馆
export const GAMES = [
  { path: '/qiqiaoban', art: 'charioteering', emoji: '🧩', name: '七巧板', desc: '7 块板拼图形' },
  { path: '/tujie', art: 'mathematics', emoji: '🐔', name: '图解数学', desc: '看图解鸡兔同笼' },
];

const P = PALETTE;
// 开门 480ms + 光铺满 450ms(从 380ms 开始),再跳转
const ENTER_MS = 900;
const prefersReducedMotion = () =>
  typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const hallOf = (id) => HALLS[id] || HALLS.music;
// 墨色硬阴影:保留「可以按」的手感,但比纯黑柔和
const inkShadow = (n = 3) => ({ boxShadow: `${n}px ${n}px 0 0 ${P.ink}` });

function Seal({ art, size = 'md' }) {
  const hall = hallOf(art.id);
  const lit = art.mastered > 0;
  const box = size === 'lg' ? 'h-12 w-12 text-2xl' : 'h-10 w-10 text-xl';
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-lg border-2 font-black ${box}`}
      style={{
        borderColor: lit ? P.ink : hall.color,
        background: lit ? hall.color : P.wall,
        color: lit ? '#fff' : hall.color,
      }}
    >
      {art.char}
    </span>
  );
}

// 歇山顶:两端起翘的屋檐 + 屋脊 + 几道瓦垄,颜色跟馆走
function Roof({ color }) {
  const line = { stroke: P.ink, strokeWidth: 2.5, strokeLinejoin: 'round' };
  return (
    <svg viewBox="0 0 400 64" className="block w-full" aria-hidden="true">
      <path
        d="M4 40 Q34 58 70 54 L330 54 Q366 58 396 40 Q372 44 342 30 L300 13 L100 13 L58 30 Q28 44 4 40 Z"
        fill={color}
        {...line}
      />
      {[130, 170, 210, 250, 290].map((x) => (
        <path key={x} d={`M${x - 8} 16 L${x - 14} 51`} stroke="#fff" strokeOpacity=".28" strokeWidth="3" />
      ))}
      <path d="M70 54 L330 54" stroke={P.ink} strokeOpacity=".18" strokeWidth="5" />
      <rect x="96" y="5" width="208" height="8" rx="3" fill={color} {...line} />
      <circle cx="96" cy="9" r="6" fill={P.gold} {...line} />
      <circle cx="304" cy="9" r="6" fill={P.gold} {...line} />
    </svg>
  );
}

// 一扇门 = 一门学科。没学过是原木门;学会一些挂上灯笼;全学会变金门
// opening 时两扇门板向里转开,露出门里的暖光(见 LearningMapPage 的 enter)
const DOOR_SWING = 'transform 480ms cubic-bezier(.3,.7,.4,1)';
const DOOR_GLOW = `radial-gradient(circle at 50% 60%, #FFF6D8, ${PALETTE.gold})`;

function DoorLeaf({ side, color, opening }) {
  const left = side === 'left';
  return (
    <span
      data-testid={`door-leaf-${side}`}
      className="absolute inset-y-0 w-1/2"
      style={{
        [side]: 0,
        background: color,
        [left ? 'borderRight' : 'borderLeft']: `1px solid ${P.woodEdge}`,
        transformOrigin: `${side} center`,
        transform: opening ? `rotateY(${left ? -110 : 110}deg)` : 'none',
        transition: DOOR_SWING,
      }}
    >
      <span className="absolute inset-y-0 left-1/2 w-px" style={{ background: P.woodEdge, opacity: 0.5 }} />
      <span
        className="absolute top-[58%] h-2.5 w-2.5 rounded-full border"
        style={{ [left ? 'right' : 'left']: 4, borderColor: P.ink, background: P.gold }}
      />
    </span>
  );
}

function Door({ d, onOpen, opening }) {
  const s = SUBJECTS[d.dimension] || { icon: '✨', tagline: '' };
  const done = d.total > 0 && d.mastered >= d.total;
  const started = d.mastered > 0;
  const leaf = done ? P.doneDoor : P.wood;
  return (
    <button
      type="button"
      data-testid={`dimension-${d.dimension}`}
      onClick={(e) => onOpen(e.currentTarget)}
      className="group flex w-[88px] flex-col items-center gap-1 active:translate-y-0.5"
    >
      <span className="h-5 text-base leading-5">{done ? '✨' : started ? '🏮' : ''}</span>
      <span
        className="z-10 -mb-2 whitespace-nowrap rounded border-2 px-1.5 text-sm font-extrabold leading-5"
        style={{ borderColor: P.ink, background: P.wall, color: P.ink }}
      >
        {d.label}
      </span>
      <span
        data-door
        className="relative flex h-24 w-[84px] justify-center overflow-hidden rounded-t-[42px] border-[2.5px]"
        style={{ borderColor: P.ink, background: DOOR_GLOW, perspective: '260px', ...inkShadow(3) }}
      >
        <DoorLeaf side="left" color={leaf} opening={opening} />
        <DoorLeaf side="right" color={leaf} opening={opening} />
        <span
          className="relative mt-3 flex h-9 w-9 items-center justify-center rounded-full border-2 text-xl"
          style={{
            borderColor: P.ink,
            background: P.wall,
            opacity: opening ? 0 : 1,
            transform: opening ? 'scale(.6)' : 'none',
            transition: 'opacity 200ms, transform 200ms',
          }}
        >
          {s.icon}
        </span>
      </span>
      <span className="text-xs leading-4" style={{ color: P.inkSoft }}>
        {s.tagline}
      </span>
      <span className="text-xs font-bold leading-4" style={{ color: P.ink }}>
        {d.mastered}/{d.total}
      </span>
    </button>
  );
}

// 小游戏 = 园林里的月洞门:圆门、砖框,一看就和学科的木门不一样,也不计进度
function MoonGate({ g, onOpen }) {
  return (
    <button
      type="button"
      data-testid={`game-${g.path.slice(1)}`}
      onClick={(e) => onOpen(e.currentTarget)}
      className="group flex w-[88px] flex-col items-center gap-1 active:translate-y-0.5"
    >
      <span className="h-5" />
      <span
        className="z-10 -mb-2 whitespace-nowrap rounded-full border-2 px-1.5 text-sm font-extrabold leading-5"
        style={{ borderColor: P.ink, background: P.wall, color: P.ink }}
      >
        {g.name}
      </span>
      <span className="flex h-24 w-[84px] items-end justify-center">
        <span
          className="flex h-[84px] w-[84px] items-center justify-center rounded-full border-[6px] text-3xl"
          style={{ borderColor: P.step, background: '#E4F0D8', outline: `2.5px solid ${P.ink}`, ...inkShadow(3) }}
        >
          {g.emoji}
        </span>
      </span>
      <span className="text-center text-xs leading-4" style={{ color: P.inkSoft }}>
        {g.desc}
      </span>
      <span className="text-xs font-bold leading-4" style={{ color: P.inkSoft }}>
        小游戏
      </span>
    </button>
  );
}

// 一座馆:屋顶 + 匾额 + 粉墙(两根檐柱、墙脚一抹馆色) + 门 + 石阶
function Hall({ a, onOpenDimension, onOpenGame, openingKey }) {
  const hall = hallOf(a.id);
  return (
    <section id={`hall-${a.id}`} data-testid={`hall-${a.id}`} className="scroll-mt-4">
      <div className="relative px-1">
        <Roof color={hall.color} />
        {/* 匾额:墨框 + 泥金内框,印章和馆名一起放大,压在屋檐和墙的交界处 */}
        <div className="absolute inset-x-0 -bottom-11 flex justify-center">
          <span className="rounded-lg border-2 p-1" style={{ borderColor: P.ink, background: P.plaque, ...inkShadow(3) }}>
            <span
              className="flex items-center gap-2.5 rounded-md border px-3 py-1"
              style={{ borderColor: P.gold }}
            >
              <span
                className="flex h-9 w-9 items-center justify-center rounded-md border-2 text-xl font-black text-white"
                style={{ background: hall.color, borderColor: P.gold }}
              >
                {a.char}
              </span>
              <span className="text-2xl font-extrabold tracking-[0.2em]" style={{ color: P.gold }}>
                {hall.name}
              </span>
            </span>
          </span>
        </div>
      </div>
      <div
        className="mx-4 border-x-[6px] px-2 pb-3 pt-14"
        style={{
          borderColor: P.pillar,
          background: `linear-gradient(to bottom, ${P.wall} 55%, ${hall.tint})`,
          outline: `2px solid ${P.ink}`,
        }}
      >
        <p className="mb-1 text-center text-xs" style={{ color: P.inkSoft }}>
          {hall.motto}
          {a.total > 0 && ` · 学会 ${a.mastered}/${a.total}`}
          {a.level && (
            <span className="ml-1 font-bold" style={{ color: hall.color }}>
              🏅 {a.level}
            </span>
          )}
        </p>
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-3">
          {a.dimensions.map((d) => (
            <Door
              key={d.dimension}
              d={d}
              opening={openingKey === d.dimension}
              onOpen={(el) => onOpenDimension(d.dimension, el)}
            />
          ))}
          {a.games.map((g) => (
            <MoonGate key={g.path} g={g} onOpen={(el) => onOpenGame(g.path, el)} />
          ))}
        </div>
      </div>
      <div className="mx-1 h-3 rounded-b border-2" style={{ borderColor: P.ink, background: P.step }} />
    </section>
  );
}

// 观心亭:五座馆之外的一座小亭子,不属于六艺、不计进度;进去照一照自己的性格
const PAVILION = { color: '#C98BA4', tint: '#F8EEF2' }; // 藕粉

function Pavilion({ onOpen, opening }) {
  return (
    <section data-testid="pavilion-guanxin" className="mx-auto w-64">
      <Roof color={PAVILION.color} />
      <div
        className="mx-5 flex items-center gap-3 border-x-[6px] px-3 pb-3 pt-2"
        style={{
          borderColor: P.pillar,
          background: `linear-gradient(to bottom, ${P.wall} 55%, ${PAVILION.tint})`,
          outline: `2px solid ${P.ink}`,
        }}
      >
        <button
          type="button"
          data-testid="guanxin-door"
          onClick={(e) => onOpen(e.currentTarget)}
          className="flex flex-col items-center active:translate-y-0.5"
        >
          <span
            data-door
            className="relative flex h-20 w-[68px] justify-center overflow-hidden rounded-t-[34px] border-[2.5px]"
            style={{ borderColor: P.ink, background: DOOR_GLOW, perspective: '220px', ...inkShadow(3) }}
          >
            <DoorLeaf side="left" color={P.wood} opening={opening} />
            <DoorLeaf side="right" color={P.wood} opening={opening} />
            <span
              className="relative mt-2.5 flex h-8 w-8 items-center justify-center rounded-full border-2 text-lg"
              style={{ borderColor: P.ink, background: P.wall, opacity: opening ? 0 : 1, transition: 'opacity 200ms' }}
            >
              🪞
            </span>
          </span>
        </button>
        <div className="flex flex-col text-left">
          <span className="text-xl font-extrabold tracking-[0.2em]" style={{ color: P.ink }}>
            观心亭
          </span>
          <span className="text-xs leading-5" style={{ color: P.inkSoft }}>
            照照观心镜
            <br />
            看看你是什么性格
          </span>
        </div>
      </div>
      <div className="h-3 rounded-b border-2" style={{ borderColor: P.ink, background: P.step }} />
    </section>
  );
}

function LearningMapPage() {
  const navigate = useNavigate();
  const [questState, , clearQuestState] = useLocalStorage('huaxia_quest_state', null);
  const [map, setMap] = useState(null);
  const [error, setError] = useState('');
  // 正在进的门:{ key, x, y } —— 门先打开,光再从门口铺满屏幕,然后跳转
  const [entering, setEntering] = useState(null);

  useEffect(() => {
    if (!questState?.sessionId) {
      navigate('/', { replace: true });
      return;
    }
    track('learning_map_view');
    fetchLearningMap(questState.sessionId)
      .then(setMap)
      .catch(() => setError('书院的门还没打开,刷新一下再试试吧'));
    // eslint-disable-next-line
  }, []);

  if (error) {
    return <p className="p-10 text-center text-red-500" role="alert">{error}</p>;
  }
  if (!map) {
    return <p className="p-10 text-center text-gray-500">正在打开六艺书院……</p>;
  }

  const guardian = map.guardian;
  const enter = (key, to, el) => {
    if (entering) return; // 动画中再点不重复触发
    if (prefersReducedMotion() || !el) {
      navigate(to);
      return;
    }
    const r = (el.querySelector('[data-door]') || el).getBoundingClientRect();
    setEntering({ key, x: r.left + r.width / 2, y: r.top + r.height * 0.6 });
    setTimeout(() => navigate(to), ENTER_MS);
  };
  const openDimension = (dimension, el) => {
    track('dimension_selected', { dimension });
    enter(dimension, `/quest?dimension=${dimension}`, el);
  };
  const openGame = (path, el) => {
    track('game_open', { path });
    enter(path, path, el);
  };
  const jumpTo = (id) => document.getElementById(`hall-${id}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const halls = map.arts
    .map((a) => ({ ...a, games: GAMES.filter((g) => g.art === a.id) }))
    .filter((a) => a.total > 0 || a.games.length > 0);

  return (
    <div className="min-h-screen px-4 py-4" style={{ background: P.paper, color: P.ink }}>
      <div className="mx-auto flex max-w-md flex-col gap-4">
        {/* 1. 灵兽问候 */}
        <header className="flex items-center gap-3">
          <span
            className="flex h-14 w-14 items-center justify-center rounded-full border-2 text-3xl"
            style={{ borderColor: P.ink, background: P.wall }}
          >
            {guardian?.stage === 'egg' ? '🥚' : speciesEmoji(guardian?.species)}
          </span>
          <div className="flex-1">
            <p className="text-lg font-extrabold">
              六艺书院
              {guardian?.stage && (
                <span
                  data-testid="guardian-stage"
                  className="ml-2 rounded-full px-2 py-0.5 align-middle text-xs font-bold"
                  style={{ background: HALLS.mathematics.tint, color: HALLS.mathematics.color }}
                >
                  {STAGE_LABEL[guardian.stage]}
                </span>
              )}
            </p>
            <p className="text-sm" style={{ color: P.inkSoft }}>
              {map.ownerName ? `${map.ownerName} 和 ` : ''}
              {guardian?.name || '守护兽'},今天想学什么?
            </p>
          </div>
        </header>

        {/* 2. 主按钮:继续冒险 —— 全页唯一的亮色块 */}
        <button
          type="button"
          data-testid="continue"
          onClick={() => navigate('/quest')}
          className="flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left active:translate-y-0.5"
          style={{ borderColor: P.ink, background: P.gold, ...inkShadow(4) }}
        >
          <span className="text-3xl">⚔️</span>
          <span className="flex flex-1 flex-col">
            {map.reviewDue > 0 ? (
              <span className="text-lg font-extrabold">{`先复习 ${map.reviewDue} 个,再继续冒险`}</span>
            ) : (
              <>
                <span className="text-lg font-extrabold">继续冒险</span>
                <span className="text-sm">接着上次的进度往下闯</span>
              </>
            )}
          </span>
          <span className="text-xl">▶</span>
        </button>
        {map.reviewDue > 0 ? (
          <p data-testid="review-due" className="-mt-2 text-center text-sm font-bold" style={{ color: HALLS.mathematics.color }}>
            🔁 今天有 {map.reviewDue} 个学过的本领该复习啦
          </p>
        ) : (
          map.nextReviewAt && (
            <p data-testid="review-next" className="-mt-2 text-center text-sm" style={{ color: P.inkSoft }}>
              🔁 {describeNextReview(map.nextReviewAt)}有本领要复习
            </p>
          )
        )}

        {/* 3. 六艺印章条:一眼看到六艺点亮了几个,点一下跳到那一馆 */}
        <nav
          data-testid="arts"
          aria-label="六艺"
          className="flex justify-between rounded-2xl px-3 py-2"
          style={{ background: P.wall, border: `1.5px dashed ${P.step}` }}
        >
          {map.arts.map((a) => {
            const soon = a.total === 0 && !GAMES.some((g) => g.art === a.id);
            return (
              <button
                key={a.id}
                type="button"
                data-testid={`art-${a.id}`}
                disabled={soon}
                onClick={() => jumpTo(a.id)}
                className={`flex flex-col items-center gap-0.5 ${soon ? 'opacity-40' : ''}`}
              >
                <Seal art={a} />
                <span className="text-xs" style={{ color: P.inkSoft }}>
                  {soon ? '待开' : a.level || '未点亮'}
                </span>
              </button>
            );
          })}
        </nav>

        {/* 4. 五座馆:每座是一栋房子,学科是门,小游戏是月洞门 */}
        <div className="flex flex-col gap-6">
          {halls.map((a) => (
            <Hall
              key={a.id}
              a={a}
              openingKey={entering?.key}
              onOpenDimension={openDimension}
              onOpenGame={openGame}
            />
          ))}
        </div>

        {/* 5. 观心亭:馆群之外的小亭子 */}
        <Pavilion
          opening={entering?.key === 'guanxin'}
          onOpen={(el) => {
            track('game_open', { path: '/guanxin' });
            enter('guanxin', '/guanxin', el);
          }}
        />

        {/* 只有设了主人名的存档才能换人 —— 没有账号的老存档退出后就回不来了 */}
        {map.ownerName && (
          <button
            type="button"
            data-testid="switch-owner"
            onClick={() => {
              if (window.confirm(getCopy(questState?.ageBand).swapOwnerConfirm(map.ownerName))) {
                clearQuestState();
                navigate('/', { replace: true });
              }
            }}
            className="self-center inline-flex min-h-[44px] items-center px-2 text-sm underline"
            style={{ color: P.inkSoft }}
          >
            {getCopy(questState?.ageBand).swapOwnerLabel(map.ownerName)}
          </button>
        )}
      </div>

      {entering && (
        <div
          data-testid="enter-glow"
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-50 animate-door-enter"
          style={{
            '--x': `${entering.x}px`,
            '--y': `${entering.y}px`,
            background: `radial-gradient(circle at ${entering.x}px ${entering.y}px, #FFF6D8, ${P.gold} 35%, ${P.paper} 75%)`,
          }}
        />
      )}
    </div>
  );
}

export default LearningMapPage;
